import { Injectable } from "@angular/core";
import { map } from "rxjs/operators";
import { HttpService } from "src/app/session/services/http.service";
import { Option } from "src/app/settings/models/option";
import { environment } from "src/environments/environment";

import { ImportDictionary } from "../../dialogs/import-event-dialog/model/import-event-inspections";
import { InspectionEvent } from "../../model/inspection-event";
import { InspectionTimelogShareMetadata } from "../../model/InspectionTimelogShareMetadata";
import { OptionResponse } from "../../model/option-response";

@Injectable({
  providedIn: "root",
})
export class EventsService {
  constructor(private httpService: HttpService) {}

  addOption(id: string, name: string): Promise<OptionResponse> {
    return this.httpService
      .post(
        `${environment.webApiUrl}/api/InspectionEvents/AddOption/${id}`,
        { optionName: name },
        { withCredentials: true },
      )
      .toPromise();
  }

  getEventList(inspectionId: string): Promise<InspectionEvent[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/InspectionEvents/EventList?inspectionId=${inspectionId}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  getEventsOptions(inspectionId: string): Promise<Option[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/InspectionDetails/EventOptions/${inspectionId}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  createEvent(inspectionEvent: InspectionEvent): Promise<void> {
    return this.httpService
      .post(`${environment.webApiUrl}/api/InspectionEvents`, inspectionEvent, {
        withCredentials: true,
      })
      .toPromise();
  }

  editEvent(inspectionEvent: any): Promise<any> {
    return this.httpService
      .put(`${environment.webApiUrl}/api/InspectionEvents`, inspectionEvent, {
        withCredentials: true,
      })
      .toPromise();
  }

  deleteEvent(eventId: string, inspectionId: string) {
    return this.httpService
      .delete(
        `${environment.webApiUrl}/api/InspectionEvents?eventId=${eventId}&inspectionId=${inspectionId}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public async getShareTimelogEventStatus(
    inspectionId: string,
  ): Promise<boolean> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/InspectionEvents/ShareTimelogEventStatus/${inspectionId}`,
        { withCredentials: true },
      )
      .pipe(
        map((shareMetadata: InspectionTimelogShareMetadata) => {
          return shareMetadata.shareTimelogEventStatus;
        }),
      )
      .toPromise();
  }

  public shareTimelog(eventId: string, inspectionId: string): Promise<void> {
    return this.httpService
      .post(
        `${environment.webApiUrl}/api/InspectionEvents/ShareSingle?inspectionId=${inspectionId}&eventId=${eventId}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public unshareTimelog(eventId: string, inspectionId: string): Promise<void> {
    return this.httpService
      .post(
        `${environment.webApiUrl}/api/InspectionEvents/UnshareSingle?inspectionId=${inspectionId}&eventId=${eventId}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public shareAllTimelog(inspectionId: string): Promise<void> {
    return this.httpService
      .post(
        `${environment.webApiUrl}/api/InspectionEvents/ShareAllTimeLog?inspectionId=${inspectionId}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public unshareAllTimelog(inspectionId: string): Promise<void> {
    return this.httpService
      .post(
        `${environment.webApiUrl}/api/InspectionEvents/UnShareAllTimeLog?inspectionId=${inspectionId}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public getImportEventsInspections(
    inspectionId: string,
  ): Promise<ImportDictionary[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/InspectionEvents/ImportableTimelogEventInspections?inspectionId=${inspectionId}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public getImportEventsItemsByInspection(
    inspectionId: string,
    withTimelogEventsAssociated: boolean,
  ): Promise<ImportDictionary[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/InspectionEvents/ImportableTimelogEventItems?inspectionId=${inspectionId}&withTimelogEventsAssociated=${withTimelogEventsAssociated}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public async importEventsFromInspection(
    sourceInspectionId: string,
    sourceItemId: string,
    targetInspectionId: string,
    targetItemId?: string,
  ): Promise<any> {
    let url = `${environment.webApiUrl}/api/InspectionEvents/ImportTimelogEvents?sourceInspectionId=${sourceInspectionId}&sourceItemId=${sourceItemId}&targetInspectionId=${targetInspectionId}`;
    if (targetItemId) {
      url = url + `&targetItemId=${targetItemId}`;
    }
    return await this.httpService
      .post(url, { withCredentials: true })
      .toPromise();
  }
}
